import type { ReactNode } from 'react';
import {
    CardPointeIpgSystemId,
    CardPointeIpgSystemIdKey,
    CloverIpgSystemId,
    CloverIpgSystemIdKey,
    defaultDemoIpgConfigByKey,
    defaultIpgConfigByKey,
    type IpgSystemId,
    type IpgSystemIdKey,
    type IpgConfig,
    type IpgConfigCustomization,
    type IpgConfigCustomizationMap,
    type MerchantConfig,
} from '@pillar-portal/api-types';
import {
    ValorIpgSystemId,
    ValorIpgSystemIdKey,
} from '@pillar-portal/api-types/ipg/valor/system-constants';

import {
    CardPointeOrganizationIpgConfigEditorAdapter,
    resolveCardPointeOrganizationIpgConfig,
    type CardPointeOrganizationIpgConfigEditorState,
} from './cardpointe/CardPointeOrganizationIpgConfigEditor';
import {
    CloverOrganizationIpgConfigEditorAdapter,
    type CloverOrganizationIpgConfigEditorState,
} from './clover/CloverOrganizationIpgConfigEditor';
import {
    ValorOrganizationIpgConfigEditorAdapter,
    type ValorOrganizationIpgConfigEditorState,
} from './valor/ValorOrganizationIpgConfigEditor';

export type OrganizationIpgConfigEditorState =
    | ValorOrganizationIpgConfigEditorState
    | CardPointeOrganizationIpgConfigEditorState
    | CloverOrganizationIpgConfigEditorState;

export type OrganizationIpgConfigEditorStateMap = Readonly<
    Record<IpgSystemIdKey, OrganizationIpgConfigEditorState>
>;

export function buildOrganizationIpgConfigEditorStateMap(
    merchantConfig: MerchantConfig | undefined
): OrganizationIpgConfigEditorStateMap {
    return {
        [ValorIpgSystemIdKey]: ValorOrganizationIpgConfigEditorAdapter.createEditableState({
            merchantConfig,
            defaultConfig: defaultIpgConfigByKey[ValorIpgSystemIdKey],
            demoConfig: defaultDemoIpgConfigByKey[ValorIpgSystemIdKey],
        }),
        [CardPointeIpgSystemIdKey]:
            CardPointeOrganizationIpgConfigEditorAdapter.createEditableState({
                merchantConfig,
                defaultConfig: defaultIpgConfigByKey[CardPointeIpgSystemIdKey],
                demoConfig: defaultDemoIpgConfigByKey[CardPointeIpgSystemIdKey],
            }),
        [CloverIpgSystemIdKey]: CloverOrganizationIpgConfigEditorAdapter.createEditableState({
            merchantConfig,
            defaultConfig: defaultIpgConfigByKey[CloverIpgSystemIdKey],
            demoConfig: defaultDemoIpgConfigByKey[CloverIpgSystemIdKey],
        }),
    };
}

export function resolveOrganizationIpgConfig(
    merchantConfig: MerchantConfig | undefined,
    ipgSystemIdKey: IpgSystemIdKey
): IpgConfig {
    switch (ipgSystemIdKey) {
        case CardPointeIpgSystemIdKey:
            return resolveCardPointeOrganizationIpgConfig(merchantConfig);
        case ValorIpgSystemIdKey:
        case CloverIpgSystemIdKey:
            return merchantConfig?.ipg[ipgSystemIdKey] ?? defaultIpgConfigByKey[ipgSystemIdKey];
    }
}

export function supportsOrganizationIpgAllowedWebsiteCustomization(
    ipgSystemId: IpgSystemId
): boolean {
    switch (ipgSystemId) {
        case CardPointeIpgSystemId:
            return false;
        case ValorIpgSystemId:
        case CloverIpgSystemId:
            return true;
    }
}

export function buildOrganizationIpgConfigEditorSavePayloadOrThrow(
    editorState: OrganizationIpgConfigEditorState
): IpgConfigCustomization {
    switch (editorState.ipgSystemId) {
        case ValorIpgSystemId:
            return ValorOrganizationIpgConfigEditorAdapter.buildSavePayload({
                editorState,
            });
        case CardPointeIpgSystemId:
            return CardPointeOrganizationIpgConfigEditorAdapter.buildSavePayload({
                editorState,
            });
        case CloverIpgSystemId:
            return CloverOrganizationIpgConfigEditorAdapter.buildSavePayload({
                editorState,
            });
    }
}

export function buildOrganizationIpgConfigCustomizationMapOrThrow(input: {
    readonly enabledIpgSystemIds: readonly IpgSystemId[];
    readonly editorStateMap: OrganizationIpgConfigEditorStateMap;
    readonly policyCustomizationMap: IpgConfigCustomizationMap;
}): IpgConfigCustomizationMap {
    const customizationMap: IpgConfigCustomizationMap = {};
    for (const ipgSystemId of input.enabledIpgSystemIds) {
        switch (ipgSystemId) {
            case ValorIpgSystemId: {
                const editorState = input.editorStateMap[ValorIpgSystemIdKey];
                const policyCustomization = input.policyCustomizationMap[ValorIpgSystemIdKey];
                if (editorState.ipgSystemId !== ValorIpgSystemId) {
                    throw new Error('Valor editor state does not match its map key.');
                }
                if (policyCustomization?.ipg_id !== ValorIpgSystemId) {
                    throw new Error('Valor policy customization does not match its map key.');
                }
                const providerPayload = ValorOrganizationIpgConfigEditorAdapter.buildSavePayload({
                    editorState,
                });
                customizationMap[ValorIpgSystemIdKey] = {
                    ...providerPayload,
                    ...(providerPayload.configuration_mode === 'CUSTOM'
                        ? {
                              urlPattern: policyCustomization.urlPattern,
                              allowedWebsites: policyCustomization.allowedWebsites,
                          }
                        : {}),
                };
                break;
            }
            case CardPointeIpgSystemId: {
                const editorState = input.editorStateMap[CardPointeIpgSystemIdKey];
                const policyCustomization = input.policyCustomizationMap[CardPointeIpgSystemIdKey];
                if (editorState.ipgSystemId !== CardPointeIpgSystemId) {
                    throw new Error('CardPointe editor state does not match its map key.');
                }
                if (policyCustomization?.ipg_id !== CardPointeIpgSystemId) {
                    throw new Error('CardPointe policy customization does not match its map key.');
                }
                const providerPayload =
                    CardPointeOrganizationIpgConfigEditorAdapter.buildSavePayload({
                        editorState,
                    });
                customizationMap[CardPointeIpgSystemIdKey] = {
                    ...policyCustomization,
                    environment: providerPayload.environment,
                    gateway_credential_uuid: editorState.gatewayCredentialUuid,
                    portalUrl: providerPayload.allowedWebsites[0]?.url,
                    urlPattern: providerPayload.urlPattern,
                    allowedWebsites: providerPayload.allowedWebsites.map((allowedWebsite) => ({
                        allowedUrlPatterns: allowedWebsite.allowedUrlPatterns,
                        sessionPersistence: allowedWebsite.sessionPersistence,
                        promoteSessionCookies: allowedWebsite.promoteSessionCookies,
                        urlPatternsRules: allowedWebsite.urlPatternsRules,
                        fetchDefaultAction: allowedWebsite.fetchDefaultAction,
                        iframeUrlPatterns: allowedWebsite.iframeUrlPatterns,
                        iframeDefaultAction: allowedWebsite.iframeDefaultAction,
                    })),
                };
                break;
            }
            case CloverIpgSystemId: {
                const editorState = input.editorStateMap[CloverIpgSystemIdKey];
                const policyCustomization = input.policyCustomizationMap[CloverIpgSystemIdKey];
                if (editorState.ipgSystemId !== CloverIpgSystemId) {
                    throw new Error('Clover editor state does not match its map key.');
                }
                if (policyCustomization?.ipg_id !== CloverIpgSystemId) {
                    throw new Error('Clover policy customization does not match its map key.');
                }
                const providerPayload = CloverOrganizationIpgConfigEditorAdapter.buildSavePayload({
                    editorState,
                });
                customizationMap[CloverIpgSystemIdKey] = {
                    ipg_id: CloverIpgSystemId,
                    environment: providerPayload.environment,
                };
                break;
            }
            default: {
                const exhaustiveIpgSystemId: never = ipgSystemId;
                throw new Error(`Unsupported IPG config editor adapter: ${exhaustiveIpgSystemId}`);
            }
        }
    }
    return customizationMap;
}

export function renderOrganizationIpgConfigEditor(input: {
    readonly organizationUuid: string;
    readonly editorState: OrganizationIpgConfigEditorState;
    readonly onChange: (nextState: OrganizationIpgConfigEditorState) => void;
    readonly disabled: boolean;
    readonly productionPresetAvailable: boolean;
}): ReactNode {
    switch (input.editorState.ipgSystemId) {
        case ValorIpgSystemId:
            return ValorOrganizationIpgConfigEditorAdapter.renderEditable({
                organizationUuid: input.organizationUuid,
                editorState: input.editorState,
                onChange: (nextState) => input.onChange(nextState),
                disabled: input.disabled,
                productionPresetAvailable: input.productionPresetAvailable,
            });
        case CardPointeIpgSystemId:
            return CardPointeOrganizationIpgConfigEditorAdapter.renderEditable({
                organizationUuid: input.organizationUuid,
                editorState: input.editorState,
                onChange: (nextState) => input.onChange(nextState),
                disabled: input.disabled,
                productionPresetAvailable: input.productionPresetAvailable,
            });
        case CloverIpgSystemId:
            return CloverOrganizationIpgConfigEditorAdapter.renderEditable({
                organizationUuid: input.organizationUuid,
                editorState: input.editorState,
                onChange: (nextState) => input.onChange(nextState),
                disabled: input.disabled,
                productionPresetAvailable: input.productionPresetAvailable,
            });
    }
}

export function renderOrganizationIpgReadOnlySummary(input: {
    readonly ipgSystemId: IpgSystemId;
    readonly organizationUuid: string;
    readonly organizationName: string | null;
    readonly merchantConfig: MerchantConfig | undefined;
    readonly providerAdministrationEnabled: boolean;
    readonly delegatedProviderSetupEnabled: boolean;
}): ReactNode {
    switch (input.ipgSystemId) {
        case ValorIpgSystemId:
            return ValorOrganizationIpgConfigEditorAdapter.renderReadOnlySummary({
                organizationUuid: input.organizationUuid,
                organizationName: input.organizationName,
                merchantConfig: input.merchantConfig,
                defaultConfig: defaultIpgConfigByKey[ValorIpgSystemIdKey],
                providerAdministrationEnabled: input.providerAdministrationEnabled,
                delegatedProviderSetupEnabled: input.delegatedProviderSetupEnabled,
            });
        case CardPointeIpgSystemId:
            return CardPointeOrganizationIpgConfigEditorAdapter.renderReadOnlySummary({
                organizationUuid: input.organizationUuid,
                organizationName: input.organizationName,
                merchantConfig: input.merchantConfig,
                defaultConfig: defaultIpgConfigByKey[CardPointeIpgSystemIdKey],
                providerAdministrationEnabled: input.providerAdministrationEnabled,
                delegatedProviderSetupEnabled: input.delegatedProviderSetupEnabled,
            });
        case CloverIpgSystemId:
            return CloverOrganizationIpgConfigEditorAdapter.renderReadOnlySummary({
                organizationUuid: input.organizationUuid,
                organizationName: input.organizationName,
                merchantConfig: input.merchantConfig,
                defaultConfig: defaultIpgConfigByKey[CloverIpgSystemIdKey],
                providerAdministrationEnabled: input.providerAdministrationEnabled,
                delegatedProviderSetupEnabled: input.delegatedProviderSetupEnabled,
            });
        default: {
            const exhaustiveIpgSystemId: never = input.ipgSystemId;
            throw new Error(`Unsupported IPG config editor adapter: ${exhaustiveIpgSystemId}`);
        }
    }
}
